import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import get from 'lodash-es/get';
import { map } from 'rxjs/operators';

import { SessionStorage } from '@finder/shared/services/session-storage/session-storage.service';
import { CONFIG_CONSTANTS, CONFIG_VALUES } from '@finder/shared/constants/app.constants';
import getApiBase from '@finder/shared/utils/string/getApiBase';
import { SimpleHttpRequest } from './config.request.service';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';

export const STAGE_CDN = 'https://stage.cdn1.parksmedia.wdprapps.disney.com';

@Injectable({
    providedIn: 'root'
})
export class ConfigService {
    config: Record<string, any>;
    configsPath = 'config';

    private dataKey = makeStateKey<object>('server config');

    constructor(
        private sessionStorage: SessionStorage,
        private configRequest: SimpleHttpRequest,
        private transferState: TransferState,
        private ssrService: SsrService,
        @Optional() @Inject(CONFIG_VALUES) private injectedConfig: object,
    ) {}

    load() {
        return new Promise((resolve, reject) => {
            const result = this.parseConfig();

            // Handle the success/fail condition
            (result instanceof SyntaxError) ? reject(result) : resolve(result);
        });
    }

    // Create a method that loads the value from storage outside of a promise

    getValue(key: string): any {
        // Get value of config key:
        return get(this.config, key);
    }

    getSiteId() {
        return get(this.config, CONFIG_CONSTANTS.siteIdKey);
    }

    getCdnUrl() {
        // Check the env values first
        let [ cdnDomain ] = this.getValue(CONFIG_CONSTANTS.cdnListKey) as string[];
        // It will be blank if we're in local dev. Revert to fallback
        if (!cdnDomain) {
            cdnDomain = STAGE_CDN;
        }

        return cdnDomain;
    }

    getApiConfigs(): any {
        const baseUrl = getApiBase(
            this.getValue(CONFIG_CONSTANTS.apiUrlKey),
            this.getValue(CONFIG_CONSTANTS.defaultVersionKey)
        );
        const configsUrl = `${baseUrl}/${this.configsPath}`;

        return this.configRequest.getRequest(configsUrl).pipe(map((data)=> ({data, configsUrl})));
    }

    getLoggerConfig() {
        let result;
        const options = {
            enabled: true,
            debug: false,
        };

        // Check to see if the config exists
        if (!this.config) {
            result = this.parseConfig();
        }

        // Stop now and return the default
        if (result instanceof SyntaxError) {
            return options;
        }

        // Check for the option we want
        options.enabled = this.getValue('loggerEnabled');

        // If SSR then we want to turn logging off. It accesses the window object
        if (this.ssrService.isSSR()) {
            options.enabled = false;
        }

        return options;
    }

    isDevelopment(): boolean {
        const environment = this.getValue('environment');

        return environment === CONFIG_CONSTANTS.developmentEnv;
    }

    private parseConfig(): object | SyntaxError {
        try {
            const configFromServer = this.transferState.get(this.dataKey, void 0);

            // We've already parsed it
            if (this.config) {
                return this.config;
            }

            // This values comes in via ssr
            if (this.injectedConfig) {
                this.config = this.injectedConfig;

                // Store the config for the browser
                this.transferState.set(this.dataKey, this.config);

                return this.config;
            }

            // Check the transfer state
            if (configFromServer) {
                this.config = configFromServer;

                return this.config;
            }

            const angularConfigValue = this.sessionStorage.get('finderAngularConfig');
            const finderAngularConfig = JSON.parse(angularConfigValue);
            this.config = finderAngularConfig;

            return this.config;
        } catch(err) {
            return err;
        }
    }
}
